import {
  all,
  call,
  CallEffect,
  fork,
  put,
  PutEffect,
  takeEvery,
} from 'redux-saga/effects';
import { User } from 'oidc-client';
import { CallHistoryMethodAction, push } from 'connected-react-router';
import { getUserManager } from '@axa-fr/react-oidc-context';

import { ActionTypes } from '../../constants/ActionTypes';
import { ShowError, ShowWarning } from '../../helper/Alert';
import FaraGostarDynamicHttp from '../../api/FaraGostarDynamicHttp';
import {
  fetchUserRoomsSuccess,
  searchRoomsSuccess,
  onSelectRoom,
  fetchUserInfoSuccess,
  fetchNewRoomMessagesSuccess,
  fetchOldRoomMessagesSuccess,
  fetchAddNewRoomSuccess,
  fetchDisableSendFileInChatSuccess,
  clearUnreadMessages,
  fetchUserRoomsWithNewMessageSuccess,
  fetchUserRoomsDetailWithNewMessageSuccess,
  signalRIsDisconnected,
  searchMessagesSuccess,
  onChatFileUploadSuccess,
} from '../actions';

import { AxiosResponse } from 'axios';

interface IChatFileUpload {
  file: File;
  roomId: any;
}

// v1 <==
const getDisableSendFileInChatData = async () => {
  const http = await FaraGostarDynamicHttp();
  return await http.get('chat/disableSendFileInChat');
};

// Disable Send File In Chat
function* fetchDisableSendFileInChatRequest(): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedDisableSendFileInChat = yield call(
      getDisableSendFileInChatData,
    );
    yield put(
      fetchDisableSendFileInChatSuccess(fetchedDisableSendFileInChat.data),
    );
  } catch (error) {
    return ShowError('', error);
  }
}

export function* fetchDisableSendFileInChat() {
  yield takeEvery(
    ActionTypes.GET_DISABLE_SEND_FILE_IN_CHAT,
    fetchDisableSendFileInChatRequest,
  );
}

// chat upload
const chatFileUploadApi = async (action: IChatFileUpload) => {
  const fd = new FormData();
  fd.append('file', action.file);
  const http = await FaraGostarDynamicHttp();
  return await http.post(`chat/chatFileUpload?roomId=${action.roomId}`, fd, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  });
};

function* chatFileUploadRequest(action: any) {
  try {
    yield call(chatFileUploadApi, action.payload);
    yield put(onChatFileUploadSuccess());
  } catch (error: any) {
    console.log('err', error);
    ShowError('', error);
    yield put(onChatFileUploadSuccess());
  }
}

export function* chatFileUpload() {
  yield takeEvery(ActionTypes.CHAT_FILE_UPLOAD, chatFileUploadRequest);
}

// v2 <==

async function setAccessToken() {
  const um = (await getUserManager().getUser()) as User;

  let bearerToken = '';
  if (um) bearerToken = um.access_token;

  $.ajaxPrefilter(function (request) {
    request.headers = request.headers || {};
    request.headers = {
      Authorization: `Bearer ${bearerToken}`,
    };
  });
}

// ************************************************************************** //

const getUserRoomsData = async () => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getUserRooms();
};

function* fetchUserRoomsRequest(): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedUserRooms = yield call(getUserRoomsData);
    yield put(fetchUserRoomsSuccess(fetchedUserRooms));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* fetchUserRooms() {
  yield takeEvery(ActionTypes.GET_USER_ROOMS, fetchUserRoomsRequest);
}

// ************************************************************************** //

const getNewRoomMessagesData = async ({
  roomId,
  maxRoomMessageId,
}: {
  roomId: number;
  maxRoomMessageId: number | undefined | null;
}) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getNewRoomMessages(
    roomId,
    maxRoomMessageId ? maxRoomMessageId : 0,
  );
};

function* fetchNewRoomMessagesRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  // debugger
  try {
    const fetchedNewRoomMessages = yield call(
      getNewRoomMessagesData,
      action.payload,
    );

    yield put(
      fetchNewRoomMessagesSuccess(
        fetchedNewRoomMessages,
        action.payload.roomId,
      ),
    );
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* fetchNewRoomMessages() {
  yield takeEvery(
    ActionTypes.GET_NEW_ROOM_MESSAGES,
    fetchNewRoomMessagesRequest,
  );
}

// ************************************************************************** //

const getOldRoomMessagesData = async ({
  roomId,
  maxMessageId,
}: {
  roomId: number;
  maxMessageId?: number;
}) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getOldRoomMessages(
    roomId,
    maxMessageId ? maxMessageId : 0,
    15,
  );
};

function* fetchOldRoomMessagesRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void | { type: ActionTypes },
  AxiosResponse<any>
> {
  try {
    const fetchedOldRoomMessages = yield call(
      getOldRoomMessagesData,
      action.payload,
    );
    yield put(
      fetchOldRoomMessagesSuccess(
        fetchedOldRoomMessages,
        action.payload.roomId,
      ),
    );
  } catch (error) {
    console.log('err', error);
    const err = error as any;
    if (err.message.includes('Call .start() before .send()')) {
      return signalRIsDisconnected();
    }
    // return ShowError("", error);
  }
}

export function* fetchOldRoomMessages() {
  yield takeEvery(
    ActionTypes.GET_OLD_ROOM_MESSAGES,
    fetchOldRoomMessagesRequest,
  );
}

// ************************************************************************** //

const getSearchRoomsRequestData = async (text: string) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.searchRooms(text);
};

function* searchRoomsRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedSearchRooms = yield call(
      getSearchRoomsRequestData,
      action.payload,
    );
    yield put(searchRoomsSuccess(fetchedSearchRooms));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* searchRooms() {
  yield takeEvery(ActionTypes.SEARCH_ROOMS, searchRoomsRequest);
}

// ************************************************************************** //

const getSearchMessagesRequestData = async (
  roomId: number,
  maxMessageId: number,
  search: string,
) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getSearchRoomMessages(
    search,
    roomId,
    maxMessageId,
    25,
  );
};

function* searchMessagesRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedSearchMessages = yield call(
      getSearchMessagesRequestData,
      action.payload.roomId,
      action.payload.maxMessageId,
      action.payload.search,
    );
    yield put(searchMessagesSuccess(fetchedSearchMessages));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* searchMessages() {
  yield takeEvery(ActionTypes.SEARCH_MESSAGES, searchMessagesRequest);
}

// ************************************************************************** //

const getRoomByCodeRequestRequestData = async (code: string) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getRoom(code);
};

function* getRoomByCodeRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  CallHistoryMethodAction<[string, unknown?]> | void,
  AxiosResponse<any> | any
> {
  try {
    const fetchedRoomByCode = yield call(
      getRoomByCodeRequestRequestData,
      action.payload,
    );

    yield put(onSelectRoom(fetchedRoomByCode));

    if (fetchedRoomByCode.Id) {
      // debugger
      let baseUrl = '/team-work/chat?';
      baseUrl += `roomId=${fetchedRoomByCode.Id}`;

      return push(baseUrl);
    }
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* getRoomByCode() {
  yield takeEvery(ActionTypes.GET_ROOM_BY_CODE, getRoomByCodeRequest);
}

// ************************************************************************** //

const getUserInfoRequestData = async (employeeId: number) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getUserInfo(employeeId);
};

function* getUserInfoRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedUserInfo = yield call(getUserInfoRequestData, action.payload);

    yield put(fetchUserInfoSuccess(fetchedUserInfo));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* getUserInfo() {
  yield takeEvery(ActionTypes.GET_USER_INFO, getUserInfoRequest);
}

// ************************************************************************** //

const sendTextMessageRequestData = async ({
  roomId,
  text,
  replyToMessageId,
}: {
  roomId: number;
  text: string;
  replyToMessageId?: number;
}) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.sendTextMessage({
    roomId,
    text,
    replyToMessageId,
  });
};

function* sendTextMessageRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    yield call(sendTextMessageRequestData, action.payload);

    // yield put(fetchSendTextMessageSuccess(fetchedSendTextMsg.Message));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* sendTextMessage() {
  yield takeEvery(ActionTypes.SEND_TEXT_MESSAGE, sendTextMessageRequest);
}

// ************************************************************************** //
const addNewRoomRequestData = async (roomId: number) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getRoomById(roomId);
};

function* addNewRoomRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedRoom = yield call(addNewRoomRequestData, action.payload);

    yield put(fetchAddNewRoomSuccess(fetchedRoom));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* addNewRoom() {
  yield takeEvery(ActionTypes.ADD_NEW_ROOM, addNewRoomRequest);
}
// ************************************************************************** //
const markMessageAsReadRequestData = async (data: any) => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.markMassageAsRead(
    data.roomId,
    data.ids,
  );
};

function* markMessageAsReadRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedRoom = yield call(
      markMessageAsReadRequestData,
      action.payload,
    );

    yield put(clearUnreadMessages(action.payload.ids));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* markMessageAsRead() {
  yield takeEvery(ActionTypes.MARK_MESSAGE_AS_READ, markMessageAsReadRequest);
}

// ************************************************************************** //

const getUserRoomsWithNewMessageRequestData = async () => {
  await setAccessToken();
  return await $.connection.chatHubV2Impl.server.getUserRoomsWithNewMessage();
};

function* getUserRoomsWithNewMessageRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedUserRoomsWithNewMessage = yield call(
      getUserRoomsWithNewMessageRequestData,
    );

    yield put(
      fetchUserRoomsWithNewMessageSuccess(fetchedUserRoomsWithNewMessage),
    );
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* getUserRoomsWithNewMessage() {
  yield takeEvery(
    ActionTypes.GET_USER_ROOMS_WITH_NEW_MESSAGE,
    getUserRoomsWithNewMessageRequest,
  );
}

// ************************************************************************** //
function* getUserRoomsDetailWithNewMessageRequest(
  action: any,
): Generator<
  | CallEffect<AxiosResponse<any>>
  | PutEffect<{ type: ActionTypes; payload: any }>,
  void,
  AxiosResponse<any>
> {
  try {
    const fetchedRoom = yield call(addNewRoomRequestData, action.payload);

    yield put(fetchUserRoomsDetailWithNewMessageSuccess(fetchedRoom));
  } catch (error) {
    console.log('err', error);
    // return ShowError("", error);
  }
}

export function* getUserRoomsDetailWithNewMessage() {
  yield takeEvery(
    ActionTypes.GET_USER_ROOMS_DETAIL_WITH_NEW_MESSAGE,
    getUserRoomsDetailWithNewMessageRequest,
  );
}
// ************************************************************************** //

export default function* rootSaga() {
  yield all([
    // v1 <==
    fork(fetchDisableSendFileInChat),
    fork(chatFileUpload),
    // v2 <==
    fork(fetchUserRooms),
    fork(fetchNewRoomMessages),
    fork(fetchOldRoomMessages),
    fork(searchRooms),
    fork(getRoomByCode),
    fork(getUserInfo),
    fork(sendTextMessage),
    fork(addNewRoom),
    fork(markMessageAsRead),
    fork(getUserRoomsWithNewMessage),
    fork(getUserRoomsDetailWithNewMessage),
    fork(searchMessages),
  ]);
}
